/**
 * Control Panel Component
 *
 * The control panel at the top of the Inventory page. Organized into 4 rows:
 * 1. Display Options - Bucket granularity, items per page
 * 2. Filters - Search (inventory/SKU), country, manufacturer
 * 3. Dates - Start/end date, run rate mode (rolling/fixed)
 * 4. Actions & Pagination - Manage POs, Save Settings, page navigation
 *
 * This component is purely presentational - all state is passed in via props.
 * Callbacks handle state updates in the parent component.
 *
 * UI Notes:
 * - All controls use MUI "small" size for compact layout
 * - Typography labels provide visual grouping (Display:, Filters:, etc.)
 * - Responsive wrapping via flexWrap: "wrap"
 */

import React from "react";
import moment from "moment";
import {
  Box,
  Button,
  Checkbox,
  FormControl,
  IconButton,
  InputLabel,
  ListItemText,
  MenuItem,
  OutlinedInput,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import {
  BucketGranularity,
  BUCKET_OPTIONS,
  GROUPINGS_PER_PAGE_OPTIONS,
  RunRateBasisMode,
} from "../types";

interface ControlPanelProps {
  // Display options
  bucketGranularity: BucketGranularity;
  onBucketChange: (value: BucketGranularity) => void;
  groupingsPerPage: number;
  onGroupingsPerPageChange: (value: number) => void;

  // Filters
  inventoryFilterText: string;
  onInventoryFilterChange: (value: string) => void;
  selectedCountries: string[];
  onCountriesChange: (value: string[]) => void;
  allCountries: string[];
  selectedManufacturers: string[];
  onManufacturersChange: (value: string[]) => void;
  allManufacturers: string[];
  selectedBrands: string[];
  onBrandsChange: (value: string[]) => void;
  allBrands: string[];

  // Dates
  startDate: string;
  onStartDateChange: (value: string) => void;
  endDate: string;
  onEndDateChange: (value: string) => void;
  runRateBasisMode: RunRateBasisMode;
  onRunRateBasisModeChange: (value: RunRateBasisMode) => void;
  runRateBasisInput: string;
  onRunRateBasisInputChange: (value: string) => void;
  fixedRunRateStart: string | null;
  onFixedRunRateStartChange: (value: string) => void;
  fixedRunRateEnd: string | null;
  onFixedRunRateEndChange: (value: string) => void;

  // Actions
  consolidatedPOsCount: number;
  onOpenPODialog: () => void;
  onOpenLeadTimesDialog: () => void;
  onSaveSettings: () => void;
  isSavingSettings: boolean;
  onRefresh: () => void;
  isRefreshing: boolean;

  // Dirty indicator for refresh button
  isSettingsDirty?: boolean;

  // Pagination
  currentPageIndex: number;
  totalPages: number;
  totalItems: number;
  onPageChange: (pageIndex: number) => void;
}

export default function ControlPanel({
  bucketGranularity,
  onBucketChange,
  groupingsPerPage,
  onGroupingsPerPageChange,
  inventoryFilterText,
  onInventoryFilterChange,
  selectedCountries,
  onCountriesChange,
  allCountries,
  selectedManufacturers,
  onManufacturersChange,
  allManufacturers,
  selectedBrands,
  onBrandsChange,
  allBrands,
  startDate,
  onStartDateChange,
  endDate,
  onEndDateChange,
  runRateBasisMode,
  onRunRateBasisModeChange,
  runRateBasisInput,
  onRunRateBasisInputChange,
  fixedRunRateStart,
  onFixedRunRateStartChange,
  fixedRunRateEnd,
  onFixedRunRateEndChange,
  consolidatedPOsCount,
  onOpenPODialog,
  onOpenLeadTimesDialog,
  onSaveSettings,
  isSavingSettings,
  onRefresh,
  isRefreshing,
  isSettingsDirty,
  currentPageIndex,
  totalPages,
  totalItems,
  onPageChange,
}: ControlPanelProps): JSX.Element {
  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mb: 2 }}>
      {/* Row 1: Display Options */}
      <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", alignItems: "center" }}>
        <Typography variant="subtitle2" sx={{ opacity: 0.7, minWidth: 80 }}>Display:</Typography>
        <FormControl size="small" sx={{ minWidth: 140 }}>
          <InputLabel>Bucket</InputLabel>
          <Select
            value={bucketGranularity}
            label="Bucket"
            onChange={(event) => onBucketChange(event.target.value as BucketGranularity)}
          >
            {BUCKET_OPTIONS.map((bucketOption) => (
              <MenuItem key={bucketOption.value} value={bucketOption.value}>
                {bucketOption.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel>Items per page</InputLabel>
          <Select
            value={groupingsPerPage}
            label="Items per page"
            onChange={(event) => onGroupingsPerPageChange(Number(event.target.value))}
          >
            {GROUPINGS_PER_PAGE_OPTIONS.map((optionValue) => (
              <MenuItem key={optionValue} value={optionValue}>
                {optionValue}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

      </Box>

      {/* Row 2: Filters + Pagination */}
      <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", alignItems: "center" }}>
        <Typography variant="subtitle2" sx={{ opacity: 0.7, minWidth: 80 }}>Filters:</Typography>
        <TextField
          size="small"
          label="Search Inventory or SKU"
          value={inventoryFilterText}
          onChange={(event) => onInventoryFilterChange(event.target.value)}
          placeholder="Search by name, ID, or SKU..."
          sx={{ minWidth: 280 }}
        />

        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel>Country</InputLabel>
          <Select
            multiple
            value={selectedCountries}
            onChange={(event) => {
              const value = event.target.value;
              onCountriesChange(typeof value === 'string' ? value.split(',') : value);
            }}
            input={<OutlinedInput label="Country" />}
            renderValue={(selected) => selected.length === allCountries.length ? 'All' : selected.join(', ')}
          >
            {allCountries.map((country) => (
              <MenuItem key={country} value={country}>
                <Checkbox checked={selectedCountries.indexOf(country) > -1} />
                <ListItemText primary={country} />
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel>Manufacturer</InputLabel>
          <Select
            multiple
            value={selectedManufacturers}
            onChange={(event) => {
              const value = event.target.value;
              onManufacturersChange(typeof value === 'string' ? value.split(',') : value);
            }}
            input={<OutlinedInput label="Manufacturer" />}
            renderValue={(selected) => selected.length === allManufacturers.length ? 'All' : selected.join(', ')}
          >
            {allManufacturers.map((manufacturer) => (
              <MenuItem key={manufacturer} value={manufacturer}>
                <Checkbox checked={selectedManufacturers.indexOf(manufacturer) > -1} />
                <ListItemText primary={manufacturer} />
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel>Brand</InputLabel>
          <Select
            multiple
            value={selectedBrands}
            onChange={(event) => {
              const value = event.target.value;
              onBrandsChange(typeof value === 'string' ? value.split(',') : value);
            }}
            input={<OutlinedInput label="Brand" />}
            renderValue={(selected) => selected.length === allBrands.length ? 'All' : selected.join(', ')}
          >
            {allBrands.map((brand) => (
              <MenuItem key={brand} value={brand}>
                <Checkbox checked={selectedBrands.indexOf(brand) > -1} />
                <ListItemText primary={brand} />
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <Box sx={{ display: "flex", gap: 1, alignItems: "center", ml: "auto" }}>
          <Typography variant="body2" sx={{ opacity: 0.8 }}>
            Page {Math.min(currentPageIndex + 1, totalPages)} / {totalPages} • {totalItems} Items
          </Typography>

          <IconButton
            size="small"
            onClick={() => onPageChange(Math.max(0, currentPageIndex - 1))}
            disabled={currentPageIndex <= 0}
          >
            <ChevronLeftIcon fontSize="small" />
          </IconButton>

          <FormControl size="small" sx={{ minWidth: 80 }}>
            <InputLabel>Page</InputLabel>
            <Select
              value={Math.min(currentPageIndex, totalPages - 1)}
              label="Page"
              onChange={(event) => onPageChange(Number(event.target.value))}
            >
              {Array.from({ length: totalPages }).map((_, pageIndex) => (
                <MenuItem key={pageIndex} value={pageIndex}>
                  {pageIndex + 1}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <IconButton
            size="small"
            onClick={() => onPageChange(Math.min(totalPages - 1, currentPageIndex + 1))}
            disabled={currentPageIndex >= totalPages - 1}
          >
            <ChevronRightIcon fontSize="small" />
          </IconButton>
        </Box>
      </Box>

      {/* Row 3: Date Range & Run Rate */}
      <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", alignItems: "center" }}>
        <Typography variant="subtitle2" sx={{ opacity: 0.7, minWidth: 80 }}>Dates:</Typography>
        <DatePicker
          label="Start Date"
          value={moment(startDate)}
          onChange={(newValue) => {
            if (newValue && newValue.isValid()) {
              onStartDateChange(newValue.format("YYYY-MM-DD"));
            }
          }}
          slotProps={{
            textField: {
              size: "small",
            },
          }}
        />

        <DatePicker
          label="End Date"
          value={moment(endDate)}
          onChange={(newValue) => {
            if (newValue && newValue.isValid()) {
              onEndDateChange(newValue.format("YYYY-MM-DD"));
            }
          }}
          slotProps={{
            textField: {
              size: "small",
            },
          }}
        />

        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel>Run Rate Mode</InputLabel>
          <Select
            value={runRateBasisMode}
            label="Run Rate Mode"
            onChange={(e) => onRunRateBasisModeChange(e.target.value as RunRateBasisMode)}
          >
            <MenuItem value="rolling">Rolling Window</MenuItem>
            <MenuItem value="fixed">Fixed Date Range</MenuItem>
          </Select>
        </FormControl>

        {runRateBasisMode === "rolling" ? (
          <TextField
            size="small"
            label="Last X Days"
            type="number"
            value={runRateBasisInput}
            onChange={(e) => onRunRateBasisInputChange(e.target.value)}
            inputProps={{ min: 1, max: 90 }}
            sx={{ width: 120 }}
          />
        ) : (
          <>
            <DatePicker
              label="Run Rate Start"
              value={fixedRunRateStart ? moment(fixedRunRateStart) : null}
              onChange={(newValue) => {
                if (newValue && newValue.isValid()) {
                  onFixedRunRateStartChange(newValue.format("YYYY-MM-DD"));
                }
              }}
              slotProps={{
                textField: { size: "small", sx: { width: 150 } },
              }}
            />
            <DatePicker
              label="Run Rate End"
              value={fixedRunRateEnd ? moment(fixedRunRateEnd) : null}
              onChange={(newValue) => {
                if (newValue && newValue.isValid()) {
                  onFixedRunRateEndChange(newValue.format("YYYY-MM-DD"));
                }
              }}
              slotProps={{
                textField: { size: "small", sx: { width: 150 } },
              }}
            />
          </>
        )}
      </Box>

      {/* Row 4: Actions */}
      <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", alignItems: "center" }}>
        <Button
          variant={isSettingsDirty ? "contained" : "outlined"}
          size="small"
          onClick={onRefresh}
          disabled={isRefreshing}
          color={isSettingsDirty ? "primary" : "inherit"}
          sx={isSettingsDirty ? {
            animation: "pulse 2s infinite",
            "@keyframes pulse": {
              "0%": { boxShadow: "0 0 0 0 rgba(25, 118, 210, 0.4)" },
              "70%": { boxShadow: "0 0 0 6px rgba(25, 118, 210, 0)" },
              "100%": { boxShadow: "0 0 0 0 rgba(25, 118, 210, 0)" },
            },
          } : undefined}
        >
          {isRefreshing ? "Refreshing..." : isSettingsDirty ? "Refresh (settings changed)" : "Refresh"}
        </Button>

        <Button
          variant="outlined"
          size="small"
          onClick={onOpenPODialog}
        >
          Manage All POs {consolidatedPOsCount > 0 && `(${consolidatedPOsCount})`}
        </Button>

        <Button
          variant="outlined"
          size="small"
          onClick={onOpenLeadTimesDialog}
        >
          Lead Times
        </Button>

        <Button
          variant="contained"
          size="small"
          onClick={onSaveSettings}
          disabled={isSavingSettings}
        >
          {isSavingSettings ? "Saving..." : "Save Settings"}
        </Button>
      </Box>
    </Box>
  );
}
